import { useCallback, useState } from "react";
import { useAdminBanners } from "@/hooks/useBanner";
import { upsertBanner, deleteBanner } from "@/lib/api";
import { Plus, Trash2 } from "lucide-react";

import { AdminPanelHeader } from "./AdminPanelHeader";

export function BannerEditor() {
  const { data: banners, loading, refetch } = useAdminBanners();
  const [editing, setEditing] = useState<{
    id?: string;
    text: string;
    link_text: string;
    link_url: string;
    is_active: boolean;
  } | null>(null);
  const [saving, setSaving] = useState(false);

  const startNew = () =>
    setEditing({ text: "", link_text: "", link_url: "", is_active: false });

  const startEdit = useCallback(
    (banner: NonNullable<typeof banners>[number]) =>
      setEditing({
        id: banner.id,
        text: banner.text,
        link_text: banner.link_text ?? "",
        link_url: banner.link_url ?? "",
        is_active: banner.is_active,
      }),
    [],
  );

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    await upsertBanner({
      id: editing.id,
      text: editing.text,
      link_text: editing.link_text || undefined,
      link_url: editing.link_url || undefined,
      is_active: editing.is_active,
    });
    setSaving(false);
    setEditing(null);
    refetch();
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this banner?")) return;
    await deleteBanner(id);
    refetch();
  };

  const handleToggleActive = async (banner: NonNullable<typeof banners>[number]) => {
    await upsertBanner({
      id: banner.id,
      text: banner.text,
      is_active: !banner.is_active,
    });
    refetch();
  };

  return (
    <div>
      <div className="mb-6">
        <AdminPanelHeader
          title="Announcement Banners"
          description="The homepage shows one announcement banner at a time. Setting a banner active will automatically make the others inactive."
          action={
            <button
              type="button"
              onClick={startNew}
              className="inline-flex items-center gap-2 rounded-xl bg-pink-dark px-4 py-2 text-sm font-semibold text-white transition-transform hover:-translate-y-px active:scale-95"
            >
              <Plus className="h-4 w-4" />
              New Banner
            </button>
          }
        />
      </div>

      {editing && (
        <div className="mb-6 rounded-xl border border-gray-200 bg-white p-5">
          <div className="mb-4 grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Banner Text
              </label>
              <input
                type="text"
                value={editing.text}
                onChange={(e) =>
                  setEditing((p) => p && { ...p, text: e.target.value })
                }
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pink-dark focus:outline-none focus:ring-1 focus:ring-pink-dark"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Link Text
              </label>
              <input
                type="text"
                value={editing.link_text}
                onChange={(e) =>
                  setEditing((p) => p && { ...p, link_text: e.target.value })
                }
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pink-dark focus:outline-none focus:ring-1 focus:ring-pink-dark"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Link URL
              </label>
              <input
                type="text"
                value={editing.link_url}
                onChange={(e) =>
                  setEditing((p) => p && { ...p, link_url: e.target.value })
                }
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pink-dark focus:outline-none focus:ring-1 focus:ring-pink-dark"
              />
            </div>
            <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
              <button
                type="button"
                role="switch"
                aria-checked={editing.is_active}
                onClick={() =>
                  setEditing((p) => p && { ...p, is_active: !p.is_active })
                }
                className={`inline-flex min-w-[9.75rem] items-center justify-start gap-2 whitespace-nowrap rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-pink-dark/30 ${
                  editing.is_active
                    ? "border-pink-dark bg-pink-dark text-white hover:bg-pink-darker"
                    : "border-gray-300 bg-white text-gray-700 hover:border-pink-dark/40 hover:text-pink-dark"
                }`}
              >
                <span
                  className={`flex h-6 w-11 rounded-full p-0.5 transition-colors ${
                    editing.is_active ? "justify-end bg-white/30" : "justify-start bg-gray-300"
                  }`}
                >
                  <span className="h-5 w-5 rounded-full bg-white shadow-sm" />
                </span>
                {editing.is_active ? "De-Activate" : "Activate"}
              </button>
              <span className="text-sm font-medium text-gray-700">
                Show this banner and turn off any other active banner.
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !editing.text.trim()}
              className="rounded-xl bg-pink-dark px-4 py-2 text-sm font-semibold text-white transition-transform hover:-translate-y-px active:scale-95 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              onClick={() => setEditing(null)}
              className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {loading ? (
          Array.from({ length: 3 }).map((_, index) => (
            <div
              key={`banner-loading-${index}`}
              className="flex animate-pulse items-center justify-between gap-4 rounded-xl border border-gray-200 bg-white p-4"
            >
              <div className="min-w-0 flex-1 space-y-2">
                <div className="h-4 w-3/4 rounded bg-gray-200" />
                <div className="h-3 w-1/3 rounded bg-gray-100" />
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <div className="h-8 w-36 rounded-full bg-gray-100" />
                <div className="h-8 w-12 rounded-lg bg-gray-100" />
                <div className="h-8 w-8 rounded-lg bg-gray-100" />
              </div>
            </div>
          ))
        ) : (
          (banners ?? []).map((banner) => (
            <div
              key={banner.id}
              className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 bg-white p-4"
            >
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium">{banner.text}</p>
              {banner.link_text && (
                <p className="text-sm text-gray-500">
                  {banner.link_text} → {banner.link_url}
                </p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                role="switch"
                aria-checked={banner.is_active}
                aria-label={`${banner.is_active ? "De-Activate" : "Activate"} banner`}
                onClick={() => handleToggleActive(banner)}
                className={`inline-flex min-w-[9rem] items-center justify-start gap-2 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-pink-dark/30 ${
                  banner.is_active
                    ? "border-pink-dark bg-pink-dark text-white hover:bg-pink-darker"
                    : "border-gray-300 bg-white text-gray-700 hover:border-pink-dark/40 hover:text-pink-dark"
                }`}
              >
                <span
                  className={`flex h-5 w-9 rounded-full p-0.5 transition-colors ${
                    banner.is_active ? "justify-end bg-white/30" : "justify-start bg-gray-300"
                  }`}
                >
                  <span className="h-4 w-4 rounded-full bg-white shadow-sm" />
                </span>
                {banner.is_active ? "De-Activate" : "Activate"}
              </button>
              <button
                type="button"
                onClick={() => startEdit(banner)}
                className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-pink-dark"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => handleDelete(banner.id)}
                className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            </div>
          ))
        )}
        {!loading && (!banners || banners.length === 0) && (
          <p className="py-8 text-center text-gray-400">No banners yet.</p>
        )}
      </div>
    </div>
  );
}
